/**
 * tt-animations.js — TwinkleTongue enterprise animation utilities
 *
 * Requires: GSAP + ScrollTrigger (loaded separately via js.liquid)
 * Exposed as: window.TTAnimations
 *
 * API:
 *   TTAnimations.lineReveal(targets, opts)   — Option 1: clip/slide-up reveal
 *   TTAnimations.wordScrub(el, opts)         — Option 2: word opacity scrub
 *   TTAnimations.blurReveal(targets, opts)   — Option 3: blur + translate entrance
 *   TTAnimations.charReveal(el, opts)        — per-character tumble-in with rotation stagger
 */
(function (global) {
	'use strict';

	var PRM = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

	/* ─── Internal helpers ─────────────────────────────────────────────────── */

	function whenReady(cb) {
		if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
			gsap.registerPlugin(ScrollTrigger);
			cb();
		} else {
			setTimeout(function () {
				whenReady(cb);
			}, 50);
		}
	}

	function toArray(targets) {
		return gsap.utils.toArray(targets).filter(Boolean);
	}

	/**
	 * Wraps each element in an overflow:hidden clip container.
	 * Respects inline vs block display. Idempotent — won't double-wrap.
	 */
	function wrapClip(el) {
		if (el.parentElement && el.parentElement.classList.contains('tt-clip'))
			return;
		var display = getComputedStyle(el).display;
		var isInline =
			display === 'inline' ||
			display === 'inline-block' ||
			display === 'inline-flex';
		var wrap = document.createElement(isInline ? 'span' : 'div');
		wrap.className = 'tt-clip';
		wrap.style.overflow = 'hidden';
		wrap.style.display = isInline ? 'inline-block' : 'block';
		el.parentNode.insertBefore(wrap, el);
		wrap.appendChild(el);
	}

	/* ─── Option 1: Clip / slide-up line reveal ────────────────────────────── */

	/**
	 * @param {string|Element|Element[]} targets
	 * @param {Object} opts
	 *   entrance   {boolean} — true = load animation (no ScrollTrigger), default false
	 *   duration   {number}  — seconds, default 0.85
	 *   ease       {string}  — default 'power3.out'
	 *   stagger    {number}  — seconds between each element, default 0.1
	 *   start      {string}  — ScrollTrigger start, default 'top 88%'
	 */
	function lineReveal(targets, opts) {
		if (PRM) return;
		opts = opts || {};
		var cfg = {
			entrance: opts.entrance || false,
			duration: opts.duration || 1.7,
			ease: opts.ease || 'power3.out',
			stagger: opts.stagger !== undefined ? opts.stagger : 0.12,
			start: opts.start || 'top 88%',
		};

		whenReady(function () {
			var els = toArray(targets);
			if (!els.length) return;

			// Pre-hide before wrap to prevent flash of visible text
			gsap.set(els, { autoAlpha: 0 });
			els.forEach(wrapClip);

			var animCfg = {
				yPercent: 108,
				autoAlpha: 0,
				duration: cfg.duration,
				ease: cfg.ease,
				stagger: cfg.stagger,
			};

			if (cfg.entrance) {
				gsap.from(els, animCfg);
			} else {
				var triggerEl = els[0].parentElement.classList.contains('tt-clip')
					? els[0].parentElement
					: els[0];
				animCfg.scrollTrigger = {
					trigger: triggerEl,
					start: cfg.start,
					toggleActions: 'play none none none',
					once: true,
				};
				gsap.from(els, animCfg);
			}
		});
	}

	/* ─── Option 2: Word opacity scrub ─────────────────────────────────────── */

	/**
	 * @param {Element} el   — single element whose text will be word-split
	 * @param {Object}  opts
	 *   start  {string} — ScrollTrigger start, default 'top 78%'
	 *   end    {string} — ScrollTrigger end,   default 'bottom 28%'
	 *   scrub  {number} — default 0.8
	 */
	function wordScrub(el, opts) {
		if (PRM || !el) return;
		opts = opts || {};
		var cfg = {
			start: opts.start || 'top 78%',
			end: opts.end || 'bottom 28%',
			scrub: opts.scrub !== undefined ? opts.scrub : 0.8,
		};

		whenReady(function () {
			// Pre-hide element before DOM surgery to prevent flash
			gsap.set(el, { autoAlpha: 0 });

			var words = el.textContent.trim().split(/\s+/);
			el.innerHTML = words
				.map(function (w) {
					return (
						'<span class="tt-w" style="display:inline-block">' + w + '</span>'
					);
				})
				.join('\u00A0'); // non-breaking space preserves word spacing without collapsing

			// Restore element visibility — spans handle their own opacity via scrub
			gsap.set(el, { autoAlpha: 1 });

			var spans = el.querySelectorAll('.tt-w');
			gsap.set(spans, { willChange: 'opacity' });

			gsap.fromTo(
				spans,
				{ opacity: 0.08 },
				{
					opacity: 1,
					stagger: { each: 0.06, from: 'start' },
					ease: 'none',
					scrollTrigger: {
						trigger: el,
						start: cfg.start,
						end: cfg.end,
						scrub: cfg.scrub,
						invalidateOnRefresh: true,
					},
					onComplete: function () {
						gsap.set(spans, { willChange: 'auto' });
					},
				},
			);
		});
	}

	/* ─── Option 3: Blur + translate reveal ────────────────────────────────── */

	/**
	 * @param {string|Element|Element[]} targets
	 * @param {Object} opts
	 *   duration  {number} — default 1.1
	 *   ease      {string} — default 'power2.out'
	 *   stagger   {number} — default 0
	 *   start     {string} — ScrollTrigger start, default 'top 82%'
	 *   y         {number} — translateY start in px, default 10
	 *   blur      {string} — CSS blur value, default '5px'
	 */
	function blurReveal(targets, opts) {
		if (PRM) return;
		opts = opts || {};
		var cfg = {
			duration: opts.duration || 2.2,
			ease: opts.ease || 'power2.out',
			stagger: opts.stagger || 0,
			start: opts.start || 'top 82%',
			y: opts.y !== undefined ? opts.y : 10,
			blur: opts.blur || '5px',
		};

		whenReady(function () {
			var els = toArray(targets);
			if (!els.length) return;

			gsap.set(els, { willChange: 'filter, transform, opacity' });

			gsap.from(els, {
				filter: 'blur(' + cfg.blur + ')',
				y: cfg.y,
				autoAlpha: 0,
				duration: cfg.duration,
				ease: cfg.ease,
				stagger: cfg.stagger,
				scrollTrigger: {
					trigger: els[0],
					start: cfg.start,
					toggleActions: 'play none none none',
					once: true,
				},
				onComplete: function () {
					gsap.set(els, { willChange: 'auto' });
				},
			});
		});
	}

	/* ─── Char reveal: per-character rise with rotation stagger ────────────── */

	/**
	 * Splits element text into individual character spans, each rising into place
	 * at a slight angle — a "tumble in" / staggered character entrance.
	 *
	 * @param {Element} el   — single element
	 * @param {Object}  opts
	 *   duration  {number} — per-character duration, default 0.6
	 *   ease      {string} — default 'power3.out'
	 *   stagger   {number} — seconds between each char, default 0.038
	 *   y         {number} — starting translateY in px, default 20
	 *   rotation  {number} — starting rotation in degrees, default 4
	 *   start     {string} — ScrollTrigger start, default 'top 85%'
	 */
	function charReveal(el, opts) {
		if (PRM || !el) return;
		opts = opts || {};
		var cfg = {
			duration: opts.duration || 1.2,
			ease: opts.ease || 'power3.out',
			stagger: opts.stagger !== undefined ? opts.stagger : 0.045,
			y: opts.y !== undefined ? opts.y : 20,
			rotation: opts.rotation !== undefined ? opts.rotation : 4,
			start: opts.start || 'top 85%',
		};

		whenReady(function () {
			// Pre-hide before DOM surgery to prevent flash of unsplit text
			gsap.set(el, { autoAlpha: 0 });

			// Split into chars — spaces get a non-breaking space to preserve layout
			var chars = el.textContent.split('');
			el.innerHTML = chars
				.map(function (c) {
					if (c === ' ')
						return '<span class="tt-c" style="display:inline-block;white-space:pre"> </span>';
					return (
						'<span class="tt-c" style="display:inline-block">' + c + '</span>'
					);
				})
				.join('');

			// Restore container visibility — chars handle their own animation
			gsap.set(el, { autoAlpha: 1 });

			var spans = el.querySelectorAll('.tt-c');
			gsap.set(spans, {
				willChange: 'transform, opacity',
				transformOrigin: '50% 100%',
			});

			gsap.from(spans, {
				y: cfg.y,
				rotation: cfg.rotation,
				autoAlpha: 0,
				duration: cfg.duration,
				ease: cfg.ease,
				stagger: { each: cfg.stagger, from: 'start' },
				scrollTrigger: {
					trigger: el,
					start: cfg.start,
					toggleActions: 'play none none none',
					once: true,
				},
				onComplete: function () {
					gsap.set(spans, { willChange: 'auto' });
				},
			});
		});
	}

	/* ─── Export ────────────────────────────────────────────────────────────── */

	global.TTAnimations = {
		lineReveal: lineReveal,
		wordScrub: wordScrub,
		blurReveal: blurReveal,
		charReveal: charReveal,
	};
})(window);
